import React, { createContext, useContext, useMemo } from "react";

import { ApolloClient, ApolloProvider, HttpLink } from "@apollo/client";
import { InMemoryCache } from "@apollo/client/cache";
import { relayStylePagination } from "@apollo/client/utilities";

import { apolloAuthLink } from "utils/apolloAuthLink";

// The v2 GraphQL API. Kept as a separate Apollo client for isolation. Reuses
// the same auth link (session cookie + CSRF + 401 handling) as the v1 client.
//
// Two pieces because Apollo has a single default client per ApolloProvider:
//   - ProvideApolloV2Client: creates the client and puts it in a context near
//     the app root, WITHOUT installing an ApolloProvider so it never shadows
//     the v1 client.
//   - WithApolloV2Client: an ApolloProvider bound to the v2 client, used around
//     the v2-ported routes.

const V2ClientContext = createContext<ApolloClient<object> | null>(null);

function v2Uri(graphqlUri: string): string {
  return graphqlUri.replace(/\/graphql\/?$/, "/graphql/v2");
}

// The non-pagination arguments to `Server.logLines`: `relayStylePagination`
// merges `first`/`after` pages within each.
const LOG_LINES_KEY_ARGS = [
  "databaseId",
  "queryFingerprint",
  "postgresRoleId",
  "classification",
  "backendPid",
  "from",
  "to",
];

export function ProvideApolloV2Client({
  children,
  graphqlUri,
}: {
  children: React.ReactNode;
  graphqlUri: string;
}) {
  const client = useMemo(
    () =>
      new ApolloClient({
        link: apolloAuthLink.concat(
          new HttpLink({ uri: v2Uri(graphqlUri), credentials: "same-origin" }),
        ),
        cache: new InMemoryCache({
          typePolicies: {
            Server: {
              fields: {
                logLines: relayStylePagination(LOG_LINES_KEY_ARGS),
              },
            },
            // Per-server groupings without an id of their own, so the cache
            // can't normalize them. Merge, so a query selecting some of their
            // fields doesn't drop the fields another query cached.
            LogAnalysis: { merge: true },
            PostgresConfig: { merge: true },
          },
        }),
      }),
    [graphqlUri],
  );

  return (
    <V2ClientContext.Provider value={client}>
      {children}
    </V2ClientContext.Provider>
  );
}

export function WithApolloV2Client({
  children,
}: {
  children: React.ReactElement;
}) {
  const client = useContext(V2ClientContext);
  if (!client) {
    throw new Error(
      "WithApolloV2Client requires an ancestor <ProvideApolloV2Client>",
    );
  }
  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}
